// makeApprovalsRuntime — the SINGLE source for the approval action family
// binding. Every method rides the screen runtime's generic `execute` funnel
// against the '_approvals' pseudo-object (or a per-object override), exactly
// like the platform's livepreview api and the standalone runtime's server api
// did in their (byte-identical) per-host wrappers. Both host adapters
// (makeDesignerScreenRuntime / makeRuntimeScreenRuntime) now build their
// `approvals` block from this one factory, so the opts→snake_case payload
// mapping lives here once instead of being duplicated in each host's api
// module + each host's screen-runtime binding block.
//
// The engine resolves the tenant DB directly from the project bound into
// `execute`, so obj_name is only used for logging on the cross-object inbox.

import type {
  ApprovalAnalytics,
  ApprovalBulkResult,
  ApprovalChange,
  ApprovalChildChange,
  ApprovalDelegation,
  ApprovalInstance,
  ApprovalListResponse,
  ApprovalOverride,
  ApprovalResponse,
  ApprovalSla,
  ApprovalSlaScanResult,
} from '../contracts'
import type { ApprovalsRuntime, TxnScreenRuntime } from './contract'

/** Build the project-bound approval action family from the host's generic
 *  `execute` funnel. `execute` is already bound to one project and routes
 *  `(objName, action, payload)` onto the host's crud/execute transport.
 *  Returns the family with EVERY member bound (Required) — the inbox contract
 *  (ApprovalsInboxApprovals) needs the full set required, so a host that
 *  builds its `approvals` from this factory satisfies the tighter
 *  ApprovalsInboxRuntime without a cast. */
export function makeApprovalsRuntime(
  execute: TxnScreenRuntime['execute'],
): Required<ApprovalsRuntime> {
  return {
    list: (opts = {}) =>
      execute(opts.objName || '_approvals', 'approval_list', {
        status: opts.status || 'pending',
        ...(opts.objName ? { obj_name: opts.objName } : {}),
        ...(opts.all ? { all: true } : {}),
        limit: opts.limit ?? 100,
        offset: opts.offset ?? 0,
      }) as Promise<ApprovalListResponse>,
    submissions: (opts = {}) =>
      execute(opts.objName || '_approvals', 'approval_submissions', {
        ...(opts.status ? { status: opts.status } : {}),
        ...(opts.objName ? { obj_name: opts.objName } : {}),
      }) as Promise<{ ok: boolean; items: ApprovalInstance[]; count: number }>,
    get: (instanceId, objName = '_approvals') =>
      execute(objName, 'approval_get', {
        instance_id: instanceId,
      }) as Promise<{
        ok: boolean
        instance: ApprovalInstance
        changes?: ApprovalChange[]
        child_changes?: ApprovalChildChange[]
        sla?: ApprovalSla
      }>,
    approve: (instanceId, opts = {}) =>
      execute(opts.objName || '_approvals', 'approval_approve', {
        instance_id: instanceId,
        ...(opts.activityId ? { activity_id: opts.activityId } : {}),
        ...(opts.comment ? { comment: opts.comment } : {}),
        ...(opts.ignoreWarnings?.length ? { ignore_warnings: opts.ignoreWarnings } : {}),
      }) as Promise<ApprovalResponse>,
    reject: (instanceId, opts = {}) =>
      execute(opts.objName || '_approvals', 'approval_reject', {
        instance_id: instanceId,
        ...(opts.activityId ? { activity_id: opts.activityId } : {}),
        ...(opts.comment ? { comment: opts.comment } : {}),
      }) as Promise<ApprovalResponse>,
    edit: (instanceId, header, opts = {}) =>
      execute(opts.objName || '_approvals', 'approval_edit', {
        instance_id: instanceId,
        header,
        ...(opts.details ? { details: opts.details } : {}),
        ...(opts.ignoreWarnings?.length ? { ignore_warnings: opts.ignoreWarnings } : {}),
      }) as Promise<ApprovalResponse>,
    withdraw: (instanceId, objName = '_approvals') =>
      execute(objName, 'approval_withdraw', {
        instance_id: instanceId,
      }) as Promise<ApprovalResponse>,
    bulk: (instanceIds, opts = {}) =>
      execute(opts.objName || '_approvals', 'approval_bulk', {
        instance_ids: instanceIds,
        decision: opts.decision || 'approve',
        ...(opts.comment ? { comment: opts.comment } : {}),
      }) as Promise<ApprovalBulkResult>,
    analytics: (objName) =>
      execute(objName || '_approvals', 'approval_analytics', {
        ...(objName ? { obj_name: objName } : {}),
      }) as Promise<{ ok: boolean; analytics: ApprovalAnalytics }>,
    delegates: {
      list: (opts = {}) =>
        execute('_approvals', 'approval_delegate_list', {
          ...(opts.all ? { all: true } : {}),
        }) as Promise<{ ok: boolean; items: ApprovalDelegation[] }>,
      set: (toUser, opts = {}) =>
        execute('_approvals', 'approval_delegate_set', {
          to_user: toUser,
          ...(opts.fromUser ? { from_user: opts.fromUser } : {}),
          ...(opts.startsAt ? { starts_at: opts.startsAt } : {}),
          ...(opts.endsAt ? { ends_at: opts.endsAt } : {}),
          ...(opts.reason ? { reason: opts.reason } : {}),
        }) as Promise<{ ok: boolean; from_user: string; to_user: string }>,
      revoke: (delegationId) =>
        execute('_approvals', 'approval_delegate_revoke', {
          delegation_id: delegationId,
        }) as Promise<{ ok: boolean }>,
    },
    overrides: {
      list: () =>
        execute('_approvals', 'approval_override_list', {}) as Promise<{
          ok: boolean
          overrides: ApprovalOverride[]
        }>,
      set: (name, value, opts = {}) =>
        execute('_approvals', 'approval_override_set', {
          name, value, ...(opts.label ? { label: opts.label } : {}),
        }) as Promise<{ ok: boolean; name: string; value: string }>,
      remove: (name) =>
        execute('_approvals', 'approval_override_delete', { name }) as Promise<{ ok: boolean }>,
    },
    slaScan: (objName) =>
      execute(objName || '_approvals', 'approval_sla_scan', {
        ...(objName ? { obj_name: objName } : {}),
      }) as Promise<ApprovalSlaScanResult>,
  }
}
